import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const CLIENT_ID = process.env.REACT_APP_APPLE_WEB_CLIENT_ID;
const REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI;

const AppleLogo = () => (
  <svg viewBox="0 0 384 512" className="h-5 w-5 fill-current" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
  </svg>
);

const AppleSignInButton = ({ requireConsent = false, consentGiven = false, termsVersion = null }) => {
  const navigate = useNavigate();
  const { appleLogin } = useAuth();
  const { toast } = useToast();
  const configRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!CLIENT_ID) return;
    let cancelled = false;
    const setup = async () => {
      try {
        const res = await fetch(`${API}/api/auth/apple/start`);
        const cfg = await res.json();
        configRef.current = cfg;
        if (window.AppleID && !cancelled) {
          window.AppleID.auth.init({
            clientId: CLIENT_ID,
            scope: 'name email',
            redirectURI: REDIRECT_URI,
            state: cfg.state,
            nonce: cfg.nonce,
            usePopup: true,
          });
          setReady(true);
        }
      } catch (e) {
        // Apple SDK or backend not ready — button stays disabled.
      }
    };
    setup();
    return () => { cancelled = true; };
  }, []);

  const handleClick = async () => {
    if (!window.AppleID) return;
    if (requireConsent && !consentGiven) {
      toast({ title: 'Please accept the Terms', description: 'You must agree to the Terms of Service and Privacy Policy to continue.', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const data = await window.AppleID.auth.signIn();
      const a = data.authorization;
      const u = data.user;
      const name = u?.name ? `${u.name.firstName || ''} ${u.name.lastName || ''}`.trim() : null;
      const user = await appleLogin({
        identity_token: a.id_token,
        authorization_code: a.code,
        state: a.state,
        nonce: configRef.current?.nonce,
        name,
        ...(requireConsent ? { accepted_terms: true, terms_version: termsVersion } : {}),
      });
      toast({ title: 'Welcome!', description: `Signed in as ${user.firstName || 'you'}.` });
      navigate('/dashboard');
    } catch (error) {
      if (error?.error !== 'popup_closed_by_user') {
        toast({ title: 'Apple Sign-In Failed', description: error?.message || 'Please try again.', variant: 'destructive' });
      }
    } finally {
      setBusy(false);
    }
  };

  if (!CLIENT_ID) return null;

  return (
    <button
      type="button"
      data-testid="apple-signin-btn"
      onClick={handleClick}
      disabled={!ready || busy}
      className="w-full rounded-full py-3.5 text-base font-medium text-black bg-white hover:bg-gray-100 active:scale-[0.99] transition-all disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center gap-2"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <AppleLogo />}
      Continue with Apple
    </button>
  );
};

export default AppleSignInButton;
